import type {
  ArithmeticWorksheetPrintRequest,
  CasReportPrintRequest,
  RealTimeReportPrintRequest,
  ReportPrintRequest,
  WorkbookPrintRequest,
} from '@mathflat/domain/RemotePrint'
import type { WorksheetReportPdfParams } from '@mathflat/domain/Report'
import { action, computed, makeObservable, observable } from 'mobx'

import { SimplePrintModal } from '@/contentComponents/modal/Print'
import { printController } from '@/controller/print.controller'
import type { Student } from '@/dto'
import { CommonError, DTO, PrintRangeOption } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'

import globalLoaderService from '../globalLoader.service'
import modalService from '../modal.service'
import { localStorageService } from '../storage.service'
import { CommonPrintService } from './commonPrint.service'

export type RealTimeReportPrintInfo = {
  studentId: Student['id']
  year: number
  month: number
}

class SimplePrintService extends CommonPrintService {
  uiStore = new UIStore()
  printTargetInfo: (number | string)[] = []
  realTimeReportPrintInfo: RealTimeReportPrintInfo[] = []
  type:
    | 'CALCULATION'
    | 'REPORT'
    | 'ONE_CLICK_REPORT'
    | 'CAS_ONE_CLICK_REPORT'
    | 'WORKBOOK_QUICK'
    | 'REPORT_REAL_TIME' = 'REPORT'
  rangeOption = new PrintRangeOption()

  constructor() {
    super()

    makeObservable<
      SimplePrintService,
      | 'setRecentPrintOption'
      | 'postMQTTPrintReport'
      | 'postPrintPrintCalculation'
      | 'postMQTTPrintReportRealTime'
      | 'postPrintCasOneClickReport'
    >(this, {
      uiStore: observable,
      printTargetInfo: observable,
      realTimeReportPrintInfo: observable,
      type: observable,
      rangeOption: observable,
      fetchPrintListInit: action,
      onPrint: action,
      isVisibleRangeError: computed,
      disabledPrint: computed,

      // private
      setRecentPrintOption: action,
      postMQTTPrintReport: action,
      postPrintPrintCalculation: action,
      postMQTTPrintReportRealTime: action,
      postPrintCasOneClickReport: action,
    })
  }

  get isVisibleRangeError() {
    if (this.uiStore.visibleRangeOption === 'ALL') {
      return false
    }

    if (this.type === 'CALCULATION') {
      return this.rangeOption.allNotSelect
    } else {
      return !this.uiStore.inputText.length
    }
  }

  get disabledPrint() {
    return this.isVisibleRangeError || false
  }

  async fetchPrintListInit(printTargetInfo: (number | string)[], type: SimplePrintService['type']) {
    try {
      const recentUiStore = localStorageService.getItem('RECENT_SIMPLE_PRINT_UI_OPTION')
      if (recentUiStore) {
        this.uiStore.update({ ...recentUiStore })
      }

      await this.fetchPrintList()
      this.printTargetInfo = printTargetInfo
      this.type = type
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async onPrint(options?: { params?: Omit<WorksheetReportPdfParams, 'presignedUrlType'> }) {
    try {
      if (!this.printAgent) {
        throw new CommonError('프린트가 연결되지 않았습니다.', { type: 'client' })
      }

      globalLoaderService.openLoader()
      switch (this.type) {
        case 'REPORT': {
          await this.postMQTTPrintReport()
          break
        }
        case 'REPORT_REAL_TIME': {
          await this.postMQTTPrintReportRealTime()
          break
        }
        case 'ONE_CLICK_REPORT':
          await this.postPrintOneClickReport(options)
          break
        case 'CAS_ONE_CLICK_REPORT':
          await this.postPrintCasOneClickReport()
          break
        case 'CALCULATION': {
          await this.postPrintPrintCalculation()
          break
        }
        case 'WORKBOOK_QUICK':
        default:
          await this.postPrintWorkbook_Quick()
          break
      }
      this.setRecentPrintOption()
      modalService.closeModal()
      toastSuccess('인쇄 요청에 성공했습니다.')
    } catch (error) {
      if (error instanceof CommonError && error.type === 'client') {
        toastError(error.message)
      }
      this.printErrorNotice(error)
    } finally {
      globalLoaderService.closeLoader()
    }
  }

  async openSimplePrintModal(
    printTargetIds: number[],
    type: SimplePrintService['type'] = 'REPORT',
    title = '보고서',
    options?: { params?: WorksheetReportPdfParams },
  ) {
    try {
      await this.fetchPrintList()
      if (this.printAgent) {
        modalService.openModal(
          <SimplePrintModal
            printTargetIds={printTargetIds}
            type={type}
            title={title}
            options={options}
          />,
        )
      }
    } catch (error) {
      this.printErrorNotice(error)
    }
  }

  async openRealTimePrintModal(realTimeReportPrintInfo: RealTimeReportPrintInfo[]) {
    try {
      await this.fetchPrintList()
      if (this.printAgent) {
        this.realTimeReportPrintInfo = realTimeReportPrintInfo
        this.type = 'REPORT_REAL_TIME'
        modalService.openModal(
          <SimplePrintModal printTargetIds={[]} type="REPORT_REAL_TIME" title="실시간 보고서" />,
        )
      }
    } catch (error) {
      this.printErrorNotice(error)
    }
  }

  private setRecentPrintOption() {
    this.setPrintOption()
    localStorageService.setItem('RECENT_SIMPLE_PRINT_UI_OPTION')(
      new UIStore({
        visibleRangeOption: this.uiStore.visibleRangeOption,
      }),
    )
  }

  private async postMQTTPrintReport() {
    // 전체를 선택한 경우 - 빈문자열을 보내면 서버에서 전체 페이지 선택해서 인쇄해줌
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''
    const reports = this.printTargetInfo.map((printTargetId) => ({
      page,
      reportId: printTargetId as string,
    }))

    if (commonRepo.user?.teacherId) {
      const data: ReportPrintRequest = {
        ...this.printOption.params,
        reports,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postMQTTPrintReport(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }

  private async postMQTTPrintReportRealTime() {
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''
    const reports = this.realTimeReportPrintInfo.map((info) => ({
      page,
      studentId: info.studentId,
      year: info.year,
      month: info.month,
    }))

    if (commonRepo.user?.teacherId) {
      const data: RealTimeReportPrintRequest = {
        ...this.printOption.params,
        reports,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postMQTTPrintReportRealTime(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }

  private async postPrintOneClickReport(options?: {
    params?: Omit<WorksheetReportPdfParams, 'presignedUrlType'>
  }) {
    // 전체를 선택한 경우 - 빈문자열을 보내면 서버에서 전체 페이지 선택해서 인쇄해줌
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''
    const reports = this.printTargetInfo.map((printTargetId) => ({
      ...options?.params,
      page,
      studentWorksheetId: printTargetId as string,
    }))

    if (commonRepo.user?.teacherId) {
      const data = {
        ...this.printOption.params,
        reports,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postPrintOneClickReport(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }

  private async postPrintCasOneClickReport() {
    // 전체를 선택한 경우 - 빈문자열을 보내면 서버에서 전체 페이지 선택해서 인쇄해줌
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''
    const reports = this.printTargetInfo.map((printTargetId) => ({
      page,
      assignedCasId: printTargetId as number,
    }))

    if (commonRepo.user?.teacherId) {
      const data: CasReportPrintRequest = {
        ...this.printOption.params,
        reports,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postPrintMQTTReportCas(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }

  private async postPrintPrintCalculation() {
    // 전체를 선택한 경우 - 빈문자열을 보내면 서버에서 전체 페이지 선택해서 인쇄해줌
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''

    const pieces = this.printTargetInfo.map((printTargetId) => ({
      page,
      pieceId: printTargetId as number,
    }))

    if (commonRepo.user?.teacherId) {
      const data: ArithmeticWorksheetPrintRequest = {
        ...this.printOption.params,
        page,
        pieces,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postMQTTPrintCalculation(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }

  private async postPrintWorkbook_Quick() {
    // 전체를 선택한 경우 - 빈문자열을 보내면 서버에서 전체 페이지 선택해서 인쇄해줌
    const page = this.uiStore.visibleRangeOption === 'SELECT' ? this.uiStore.inputText : ''
    const books = this.printTargetInfo.map((bookCode) => ({
      page,
      bookCode: bookCode as number,
    }))

    if (commonRepo.user?.teacherId) {
      const data: WorkbookPrintRequest = {
        ...this.printOption.params,
        page,
        books,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postPrintWorkbookQuick(data)
    } else {
      throw new CommonError('인쇄 요청에 실패했습니다', { type: 'client' })
    }
  }
}

class UIStore extends DTO {
  visibleRangeOption: 'ALL' | 'SELECT' = 'ALL'
  inputText = ''

  constructor(props?: Partial<UIStore>) {
    super()

    Object.assign(this, props)

    makeObservable(this, {
      visibleRangeOption: observable,
      inputText: observable,
      initData: action,
    })
  }

  initData() {
    this.visibleRangeOption = 'ALL'
    this.inputText = ''
  }
}

export const simplePrintService = new SimplePrintService()
